'use client';

import { useState } from 'react';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { EllipsisVerticalIcon, PencilIcon } from 'lucide-react';

import { PageMain } from '@/app/(app)/page-main';
import { CreatePestDialog, EditPestDialog } from '@/app/(app)/settings/pests/dialog';
import { DataTable } from '@/components/data-table';
import { ApiSchemas } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ISSUE_CATEGORY_MAP, ISSUE_COVERAGE_MAP } from '@/consts/enums';

export function PestsPage() {
  const {
    office: { issues },
  } = useOfficeCtx();

  return (
    <PageMain title='Pests'>
      <div className='flex w-full items-center justify-between'>
        <div className='flex flex-col gap-0.5'>
          <h1 className='text-heading-lg'>Pests Directory</h1>
          <p className='text-sm text-muted-foreground'>Pests that can be selected for skills and services.</p>
        </div>
        <CreatePestDialog />
      </div>
      <PestsTable pests={issues ?? []} />
    </PageMain>
  );
}

type ApiIssue = ApiSchemas['IssueRead'];

function PestsTable({ pests }: { pests: ApiIssue[] }) {
  const table = useReactTable({
    data: pests,
    columns: COLUMNS,
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <div className='overflow-hidden rounded-md border'>
      <DataTable table={table} columns={COLUMNS} className='overflow-hidden' />
    </div>
  );
}

const COLUMNS: ColumnDef<ApiIssue>[] = [
  {
    accessorKey: 'name',
    header: () => <div className='p-4'>Name</div>,
    cell: ({ row }) => {
      return <span className='p-4 font-medium'>{row.original.name}</span>;
    },
  },
  {
    header: 'Category',
    accessorKey: 'category',
    cell: ({ row }) => {
      return (
        <Badge className='capitalize' variant='outline'>
          {ISSUE_CATEGORY_MAP[row.original.category]}
        </Badge>
      );
    },
  },
  {
    header: 'Coverage',
    accessorKey: 'coverage',
    cell: ({ row }) => {
      return (
        <Badge className='capitalize' variant='outline'>
          {ISSUE_COVERAGE_MAP[row.original.coverage]}
        </Badge>
      );
    },
  },
  {
    header: 'Description',
    accessorKey: 'description',
    cell: ({ row }) => {
      return (
        <div className='max-w-[50vw] truncate text-muted-foreground'>
          <span>{row.original.description || '-'}</span>
        </div>
      );
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      return <ActionCell issue={row.original} />;
    },
  },
];

function ActionCell({ issue }: { issue: ApiIssue }) {
  const [editOpen, setEditOpen] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size='icon' variant='ghost'>
            <EllipsisVerticalIcon className='size-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onSelect={() => setEditOpen(true)}>
            <PencilIcon className='size-4 text-foreground' />
            Edit
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <EditPestDialog open={editOpen} onOpenChange={setEditOpen} issue={issue} />
    </>
  );
}
